// TennisTrack Hub — Ricerca Medical Network | 029 Project
// Filtri per specializzazione (ortopedici, fisiatri, ecc.) + geolocalizzazione circolo
import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import api from '../../utils/api';
import Navbar from '../../components/Navbar';
import './Medici.css';

// ─────────────────────────────────────────────────
// Componente: Card medico nella lista
// ─────────────────────────────────────────────────
const CardMedico = ({ medico }) => (
  <Link to={`/medici/${medico.id}`} className="medico-card" style={{ textDecoration: 'none' }}>
    <div className="medico-card-header">
      {medico.foto_url ? (
        <img src={medico.foto_url} alt={`${medico.nome} ${medico.cognome}`} className="medico-foto" />
      ) : (
        <div className="medico-foto-placeholder">
          {medico.nome?.[0]}{medico.cognome?.[0]}
        </div>
      )}
      <div className="medico-card-info">
        <div className="medico-spec-badge">
          {medico.spec_icona} {medico.spec_nome}
        </div>
        <h3 className="medico-nome">{medico.titolo ? `${medico.titolo} ` : ''}{medico.nome} {medico.cognome}</h3>
        {medico.studio_nome && (
          <div className="medico-studio">🏢 {medico.studio_nome}</div>
        )}
        <div className="medico-localita">
          📍 {medico.citta}{medico.provincia ? ` (${medico.provincia})` : ''}
          {medico.distanza_km !== undefined && (
            <span className="medico-distanza"> · {medico.distanza_km} km</span>
          )}
        </div>
      </div>
    </div>

    <div className="medico-card-body">
      {medico.curriculum && (
        <p className="medico-excerpt">
          {medico.curriculum.substring(0, 150)}{medico.curriculum.length > 150 ? '…' : ''}
        </p>
      )}
      <div className="medico-tags">
        {medico.anni_esperienza && (
          <span className="medico-tag">⏱ {medico.anni_esperienza} anni esp.</span>
        )}
        {medico.convenzioni && (
          <span className="medico-tag">🤝 Convenzionato</span>
        )}
        {medico.accetta_messaggi && (
          <span className="medico-tag medico-tag-contact">💬 Contattabile</span>
        )}
        {medico.link_prenotazione && (
          <span className="medico-tag medico-tag-book">📅 Prenota online</span>
        )}
      </div>
    </div>

    <div className="medico-card-footer">
      <span className="medico-cta">Vedi scheda completa →</span>
    </div>
  </Link>
);

// ─────────────────────────────────────────────────
// Pagina principale: Ricerca Medici
// ─────────────────────────────────────────────────
const RicercaMedici = () => {
  const [medici, setMedici] = useState([]);
  const [specializzazioni, setSpecializzazioni] = useState([]);
  const [loading, setLoading] = useState(false);
  const [errore, setErrore] = useState('');
  const [geoLoading, setGeoLoading] = useState(false);

  // Filtri
  const [filtroSpec, setFiltroSpec] = useState('');
  const [filtroCitta, setFiltroCitta] = useState('');
  const [filtroCerca, setFiltroCerca] = useState('');
  const [filtroLat, setFiltroLat] = useState('');
  const [filtroLng, setFiltroLng] = useState('');
  const [filtroRaggio, setFiltroRaggio] = useState(30);
  const [geoAttiva, setGeoAttiva] = useState(false);

  // Carica specializzazioni
  useEffect(() => {
    api.get('/medici/specializzazioni')
      .then(res => setSpecializzazioni(res.data.specializzazioni))
      .catch(() => {});
  }, []);

  // Ricerca medici
  const cercaMedici = useCallback(async () => {
    setLoading(true);
    setErrore('');
    try {
      const params = new URLSearchParams();
      if (filtroSpec)  params.set('specializzazione', filtroSpec);
      if (filtroCitta) params.set('citta', filtroCitta);
      if (filtroCerca) params.set('cerca', filtroCerca);
      if (geoAttiva && filtroLat && filtroLng) {
        params.set('lat', filtroLat);
        params.set('lng', filtroLng);
        params.set('raggio', filtroRaggio);
      }
      const res = await api.get(`/medici?${params.toString()}`);
      setMedici(res.data.medici);
    } catch (err) {
      setErrore(err.response?.data?.error || 'Errore nella ricerca.');
    } finally {
      setLoading(false);
    }
  }, [filtroSpec, filtroCitta, filtroCerca, geoAttiva, filtroLat, filtroLng, filtroRaggio]);

  // Prima ricerca e ogni cambio filtro
  useEffect(() => { cercaMedici(); }, [cercaMedici]);

  // Geolocalizzazione
  const attivaPosizione = () => {
    if (!navigator.geolocation) {
      alert('Geolocalizzazione non disponibile nel browser.');
      return;
    }
    setGeoLoading(true);
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setFiltroLat(pos.coords.latitude.toString());
        setFiltroLng(pos.coords.longitude.toString());
        setGeoAttiva(true);
        setGeoLoading(false);
      },
      () => {
        alert('Impossibile ottenere la posizione. Controlla i permessi del browser.');
        setGeoLoading(false);
      }
    );
  };

  const disattivaGeo = () => {
    setFiltroLat('');
    setFiltroLng('');
    setGeoAttiva(false);
  };

  return (
    <div className="page-layout">
      <Navbar />
      <main className="page-main">
        <div className="medici-container">

          {/* Header */}
          <div className="medici-header">
            <div className="medici-header-text">
              <h1 className="medici-title">🩺 Medical Network</h1>
              <p className="medici-subtitle">
                Trova specialisti sportivi vicino al tuo circolo — ortopedici, fisiatri, fisioterapisti e molto altro.
              </p>
            </div>
            <Link to="/medici/registra" className="btn btn-medical">
              + Registra il tuo studio
            </Link>
          </div>

          {/* Filtri */}
          <div className="medici-filtri card">
            <div className="filtri-grid">
              {/* Specializzazione */}
              <div className="filtro-group">
                <label className="filtro-label">Specializzazione</label>
                <select
                  className="form-select"
                  value={filtroSpec}
                  onChange={e => setFiltroSpec(e.target.value)}
                >
                  <option value="">Tutte le specializzazioni</option>
                  {specializzazioni.map(s => (
                    <option key={s.codice} value={s.codice}>{s.icona} {s.nome}</option>
                  ))}
                </select>
              </div>

              {/* Città */}
              <div className="filtro-group">
                <label className="filtro-label">Città</label>
                <input
                  type="text"
                  className="form-input"
                  placeholder="Es. Milano, Roma..."
                  value={filtroCitta}
                  onChange={e => setFiltroCitta(e.target.value)}
                />
              </div>

              {/* Cerca testo */}
              <div className="filtro-group">
                <label className="filtro-label">Cerca</label>
                <input
                  type="text"
                  className="form-input"
                  placeholder="Nome, studio..."
                  value={filtroCerca}
                  onChange={e => setFiltroCerca(e.target.value)}
                />
              </div>

              {/* Geolocalizzazione */}
              <div className="filtro-group">
                <label className="filtro-label">Vicino a me</label>
                {!geoAttiva ? (
                  <button
                    className="btn btn-geo"
                    onClick={attivaPosizione}
                    disabled={geoLoading}
                  >
                    {geoLoading ? '⏳ Localizzando...' : '📍 Usa la mia posizione'}
                  </button>
                ) : (
                  <div className="geo-attiva">
                    <span>📍 Posizione attiva</span>
                    <select
                      className="form-select"
                      value={filtroRaggio}
                      onChange={e => setFiltroRaggio(parseInt(e.target.value))}
                      style={{ maxWidth: '100px' }}
                    >
                      <option value={10}>10 km</option>
                      <option value={20}>20 km</option>
                      <option value={30}>30 km</option>
                      <option value={50}>50 km</option>
                      <option value={100}>100 km</option>
                    </select>
                    <button className="btn btn-sm btn-secondary" onClick={disattivaGeo}>✕</button>
                  </div>
                )}
              </div>
            </div>

            {/* Shortcuts specializzazioni rapide */}
            <div className="spec-shortcuts">
              <span className="spec-shortcut-label">Rapido:</span>
              {[
                { codice: 'ortopedico',    label: '🦴 Ortopedico' },
                { codice: 'fisiatra',      label: '🏥 Fisiatra' },
                { codice: 'fisioterapista',label: '💪 Fisioterapista' },
                { codice: 'osteopata',     label: '🖐 Osteopata' },
                { codice: 'medico_sport',  label: '🏃 Medico Sport' },
              ].map(s => (
                <button
                  key={s.codice}
                  className={`spec-chip ${filtroSpec === s.codice ? 'active' : ''}`}
                  onClick={() => setFiltroSpec(filtroSpec === s.codice ? '' : s.codice)}
                >
                  {s.label}
                </button>
              ))}
            </div>
          </div>

          {/* Risultati */}
          {errore && <div className="alert alert-error">{errore}</div>}

          {loading ? (
            <div className="loading-msg">Ricerca in corso...</div>
          ) : (
            <>
              <div className="medici-risultati-header">
                <span className="medici-count">
                  {medici.length === 0
                    ? 'Nessun medico trovato con questi filtri'
                    : `${medici.length} specialist${medici.length === 1 ? 'a' : 'i'} trovato${medici.length === 1 ? '' : 'i'}`
                  }
                </span>
                {geoAttiva && medici.length > 0 && (
                  <span className="geo-label">📍 Ordinati per distanza</span>
                )}
              </div>

              <div className="medici-grid">
                {medici.map(m => <CardMedico key={m.id} medico={m} />)}
              </div>

              {medici.length === 0 && !loading && (
                <div className="medici-vuoti card">
                  <div style={{ fontSize: '2.5rem', marginBottom: '12px' }}>🔍</div>
                  <p>Nessuno specialista trovato con questi filtri.</p>
                  <p style={{ fontSize: '0.85rem', color: 'var(--text-secondary)', marginTop: '8px' }}>
                    Prova a cambiare specializzazione, città o raggio di ricerca.
                  </p>
                </div>
              )}
            </>
          )}
        </div>
      </main>
    </div>
  );
};

export default RicercaMedici;
